import { FC, memo } from 'react'
import { Draft, DraftRequestReviewAction, DraftRequestReviewStatus } from './types/types'
import { useActionModalContext } from './Modals/ConfirmationModal/ActionModalContext'
import { useReviewDraftMutation } from './useDraftsQuery'

import { ReactComponent as IconApprove } from 'images/icons/icon-approve.svg'
import { ReactComponent as IconReject } from 'images/icons/icon-reject.svg'
import { ReactComponent as IconFlag } from 'images/icons/icon-flag.svg'
import { ReactComponent as IconComment } from 'images/icons/annotation.svg'

import styles from './ActionsMenu.module.scss'

interface ActionsMenuProps {
  draft: Draft
}

const ActionsMenu: FC<ActionsMenuProps> = ({ draft }) => {
  const { setActionModal } = useActionModalContext()
  const reviewMutation = useReviewDraftMutation()

  if (draft.status !== DraftRequestReviewStatus.PENDING && draft.status !== DraftRequestReviewStatus.FLAGGED) {
    return null
  }
  const showFlagButton = draft.status === DraftRequestReviewStatus.PENDING

  const handleDirectAction = (action: DraftRequestReviewAction) => {
    if (reviewMutation.isLoading) return

    reviewMutation.mutate(
      {
        draftId: draft.id,
        action: action,
      },
      {
        onSuccess: () => {
          // Success handled by the mutation hook
        },
        onError: (error) => {},
      },
    )
  }

  const handleCommentAction = (action: DraftRequestReviewAction) => {
    setActionModal({
      action: action,
      draft: draft,
    })
  }

  return (
    <div className={styles.actionsContainer}>
      <div className={styles.splitButton}>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.approveButton} ${styles.mainAction}`}
          onClick={() => handleDirectAction(DraftRequestReviewAction.APPROVE)}
          disabled={reviewMutation.isLoading}
          title="Approve petition directly"
        >
          <IconApprove width={16} height={16} />
          <span>{reviewMutation.isLoading ? 'Processing...' : 'Approve'}</span>
        </button>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.approveButton} ${styles.commentAction}`}
          onClick={() => handleCommentAction(DraftRequestReviewAction.APPROVE)}
          disabled={reviewMutation.isLoading}
          title="Approve with comment"
        >
          <IconComment width={14} height={14} />
        </button>
      </div>

      {showFlagButton && (
        <div className={styles.splitButton}>
          <button
            type="button"
            className={`${styles.actionButton} ${styles.flagButton} ${styles.mainAction}`}
            onClick={() => handleDirectAction(DraftRequestReviewAction.FLAG)}
            disabled={reviewMutation.isLoading}
            title="Flag petition directly"
          >
            <IconFlag width={16} height={16} />
            <span>{reviewMutation.isLoading ? 'Processing...' : 'Flag'}</span>
          </button>
          <button
            type="button"
            className={`${styles.actionButton} ${styles.flagButton} ${styles.commentAction}`}
            onClick={() => handleCommentAction(DraftRequestReviewAction.FLAG)}
            disabled={reviewMutation.isLoading}
            title="Flag with comment"
          >
            <IconComment width={14} height={14} />
          </button>
        </div>
      )}

      <div className={styles.splitButton}>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.rejectButton} ${styles.mainAction}`}
          onClick={() => handleDirectAction(DraftRequestReviewAction.REJECT)}
          disabled={reviewMutation.isLoading}
          title="Reject petition directly"
        >
          <IconReject width={16} height={16} />
          <span>{reviewMutation.isLoading ? 'Processing...' : 'Reject'}</span>
        </button>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.rejectButton} ${styles.commentAction}`}
          onClick={() => handleCommentAction(DraftRequestReviewAction.REJECT)}
          disabled={reviewMutation.isLoading}
          title="Reject with comment"
        >
          <IconComment width={14} height={14} />
        </button>
      </div>
    </div>
  )
}

export default memo(ActionsMenu)
